import React, { useMemo } from "react";

/**
 * ManuscriptAtmosphere — ambient, almost-subliminal motion layer.
 *
 * Mounted ONCE at the page root behind all content (z-index 1). It renders:
 *   • slow-drifting tobacco-leaf silhouettes (60–110s linear translation)
 *   • soft paper-dust particles (18–28s opacity pulse)
 *   • two ultra-slow smoke columns (90–140s vertical drift)
 *
 * Everything is pointer-events: none, opacity 0.05–0.18, and fully paused
 * under `prefers-reduced-motion: reduce`. On viewports ≤768px the density
 * is reduced for calm + performance.
 *
 * The user should almost not notice the movement — they should FEEL the
 * atmosphere.
 */

// Deterministic pseudo-random so the layout is stable across renders.
const rand = (seed) => {
  const x = Math.sin(seed * 9301 + 49297) * 233280;
  return x - Math.floor(x);
};

const LeafSilhouette = ({ size = 28 }) => (
  <svg
    width={size}
    height={size * 1.6}
    viewBox="0 0 100 160"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M50 8 C 18 28 8 70 18 110 C 28 144 50 154 50 154 C 50 154 72 144 82 110 C 92 70 82 28 50 8 Z"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
    />
    <path
      d="M50 14 L 50 150"
      stroke="currentColor"
      strokeWidth="0.8"
      opacity="0.8"
    />
    {/* veins */}
    {[28, 48, 72, 96, 120].map((y) => (
      <g key={y} opacity="0.6">
        <path
          d={`M50 ${y} Q ${30} ${y + 6} ${22} ${y + 14}`}
          stroke="currentColor"
          strokeWidth="0.55"
          fill="none"
        />
        <path
          d={`M50 ${y} Q ${70} ${y + 6} ${78} ${y + 14}`}
          stroke="currentColor"
          strokeWidth="0.55"
          fill="none"
        />
      </g>
    ))}
  </svg>
);

export default function ManuscriptAtmosphere() {
  // ─── Drifting leaves ──────────────────────────────────────────────
  const leaves = useMemo(() => {
    const out = [];
    for (let i = 0; i < 8; i++) {
      const r = rand(i + 1);
      const r2 = rand(i + 11);
      const r3 = rand(i + 31);
      const r4 = rand(i + 71);
      const reverse = i % 2 === 1;
      const mobileHide = i > 3; // keep only 4 leaves on mobile
      out.push({
        id: `leaf-${i}`,
        size: 22 + Math.round(r * 28), // 22–50
        top: `${Math.round(r2 * 78) + 4}%`,
        // start off the side it will drift TOWARDS away from to avoid jump
        left: reverse ? `-${Math.round(20 + r3 * 20)}vw` : `${100 + Math.round(r3 * 18)}vw`,
        duration: 70 + Math.round(r4 * 50), // 70–120s
        delay: -Math.round(r * 90), // start mid-cycle so the page is never empty
        rot: `${Math.round((r - 0.5) * 80)}deg`,
        drop: `${Math.round((r2 - 0.5) * 18)}vh`,
        spin: `${Math.round((r3 - 0.5) * 10)}deg`,
        reverse,
        mobileHide,
      });
    }
    return out;
  }, []);

  // ─── Paper dust ───────────────────────────────────────────────────
  const dust = useMemo(() => {
    const out = [];
    for (let i = 0; i < 22; i++) {
      const r = rand(i + 101);
      const r2 = rand(i + 211);
      const r3 = rand(i + 311);
      const mobileHide = i > 10;
      out.push({
        id: `dust-${i}`,
        top: `${Math.round(r * 100)}%`,
        left: `${Math.round(r2 * 100)}%`,
        duration: 18 + Math.round(r3 * 12), // 18–30s
        delay: -Math.round(r * 30),
        opacity: 0.12 + r2 * 0.12, // 0.12–0.24
        mobileHide,
      });
    }
    return out;
  }, []);

  // ─── Smoke columns ────────────────────────────────────────────────
  const smokes = useMemo(
    () => [
      {
        id: "smoke-1",
        left: "12%",
        duration: 110,
        delay: -28,
        x: "3vw",
        op: 0.08,
        mobileHide: false,
      },
      {
        id: "smoke-2",
        left: "78%",
        duration: 130,
        delay: -64,
        x: "-2vw",
        op: 0.06,
        mobileHide: true,
      },
    ],
    [],
  );

  return (
    <div
      className="ms-layer"
      aria-hidden="true"
      data-testid="manuscript-atmosphere"
    >
      {leaves.map((l) => (
        <div
          key={l.id}
          className={`ms-leaf ${l.reverse ? "ms-leaf--reverse" : ""} ${
            l.mobileHide ? "ms-leaf--mobile-hide" : ""
          }`}
          style={{
            top: l.top,
            left: l.left,
            animationDuration: `${l.duration}s`,
            animationDelay: `${l.delay}s`,
            ["--ms-rot"]: l.rot,
            ["--ms-drop"]: l.drop,
            ["--ms-spin"]: l.spin,
          }}
        >
          <LeafSilhouette size={l.size} />
        </div>
      ))}

      {dust.map((d) => (
        <div
          key={d.id}
          className={`ms-dust ${d.mobileHide ? "ms-dust--mobile-hide" : ""}`}
          style={{
            top: d.top,
            left: d.left,
            animationDuration: `${d.duration}s`,
            animationDelay: `${d.delay}s`,
            ["--ms-dust-op"]: d.opacity,
          }}
        />
      ))}

      {smokes.map((s) => (
        <div
          key={s.id}
          className={`ms-smoke ${s.mobileHide ? "ms-smoke--mobile-hide" : ""}`}
          style={{
            left: s.left,
            animationDuration: `${s.duration}s`,
            animationDelay: `${s.delay}s`,
            ["--ms-smoke-x"]: s.x,
            ["--ms-smoke-op"]: s.op,
          }}
        />
      ))}
    </div>
  );
}
